import { useInfiniteQuery } from '@tanstack/react-query';
import { directoryApi, type AuditLogEntry, type AuditActionValue } from '../client';

export const auditKeys = {
  all: ['directory', 'audit'] as const,
  forAggregate: (
    organisationId: string,
    aggregateType: string,
    aggregateId: string,
    filters?: AuditTrailFilters,
  ) => [...auditKeys.all, organisationId, aggregateType, aggregateId, filters ?? {}] as const,
};

/**
 * Narrowing for the Audit Log screen. Part of the query KEY as well as the
 * request, so changing a filter starts a new list rather than appending to the
 * pages fetched under the old one.
 */
export interface AuditTrailFilters {
  fromUtc?: string;
  toUtc?: string;
  actor?: string;
  action?: AuditActionValue;
  entityType?: string;
  search?: string;
}

/** How many entries a page carries. */
const PAGE_SIZE = 50;

/** The cursor: where a page stopped, as the server wants it back. */
interface Cursor {
  beforeUtc: string;
  beforeId: string;
}

/**
 * A record's history, newest first, a page at a time.
 *
 * <p>
 * Paged because a busy record accumulates entries for as long as it exists —
 * an order worked on for months, or a whole organisation's settings — and
 * fetching all of them to show the newest twenty is a cost that grows quietly
 * until somebody notices the screen has become slow.
 * </p>
 *
 * <p>
 * The cursor is the last entry's timestamp AND id, not an offset. This table
 * grows at the TOP, so an offset shifts under the reader: a row written between
 * two pages would push one they have already read into the next page, or hide
 * one entirely. The id is part of it because a single save writes several rows
 * with the same timestamp.
 * </p>
 *
 * <p>
 * `staleTime: 0` on purpose, unlike most reads here: the trail is opened right
 * after somebody saved, precisely to see what the save did. A cached answer from
 * before the write would show the operator their own change missing.
 * </p>
 */
export function useAuditTrail(
  organisationId: string | null,
  aggregateType: string,
  /** One record, or null for every record of that type in the organisation. */
  aggregateId: string | null,
  options?: {
    enabled?: boolean;
    pageSize?: number;
    wholeType?: boolean;
    /** Everything the caller may read, for the Audit Log screen. */
    allTypes?: boolean;
    filters?: AuditTrailFilters;
  },
) {
  // `wholeType` is explicit rather than inferred from a null id: a caller that
  // simply has not resolved its record yet must not silently fetch the whole
  // organisation's history instead.
  const wholeType = options?.wholeType ?? false;
  const allTypes = options?.allTypes ?? false;
  const filters = options?.filters;
  const enabled =
    !!organisationId && (wholeType || allTypes || !!aggregateId) && (options?.enabled ?? true);
  const pageSize = options?.pageSize ?? PAGE_SIZE;

  const query = useInfiniteQuery<
    AuditLogEntry[],
    Error,
    AuditLogEntry[],
    readonly unknown[],
    Cursor | null
  >({
    queryKey: auditKeys.forAggregate(
      organisationId ?? '',
      aggregateType,
      aggregateId ?? '',
      filters,
    ),
    initialPageParam: null,
    queryFn: ({ pageParam }) =>
      directoryApi.getAuditTrail({
        organisationId: organisationId!,
        // Omitted entirely for the Audit Log screen, which is asking for
        // everything this reader is allowed to see.
        aggregateType: allTypes ? undefined : aggregateType,
        aggregateId: aggregateId ?? undefined,
        take: pageSize,
        ...filters,
        beforeUtc: pageParam?.beforeUtc,
        beforeId: pageParam?.beforeId,
      }),
    // A short page is the end of the trail — no count query needed, and no
    // extra request to discover there is nothing left.
    getNextPageParam: (lastPage) => {
      if (lastPage.length < pageSize) return null;
      const last = lastPage[lastPage.length - 1]!;
      return { beforeUtc: last.occurredUtc, beforeId: last.id };
    },
    select: (data) => data.pages.flat(),
    enabled,
    staleTime: 0,
  });

  return query;
}
